export type OrderStageLookupEntry = {
  stageIndex: number;
  label: string | null;
};

export type OrderStageLookup = Record<string, OrderStageLookupEntry>;

const PRE_CONFIRMATION_APPROVAL_STATUSES = new Set([
  'quote',
  'budgetary',
  'approval_pending',
  'approved',
  'pending_acceptance',
]);

/**
 * Approval requests, decisions, and client sends belong to the quote lifecycle.
 * Once an event reaches `confirmed`, client acceptance has already been recorded
 * and those actions must become read-only even though operational editing remains
 * available.
 */
export function isPreConfirmationApprovalStatus(status: string | null | undefined): boolean {
  return PRE_CONFIRMATION_APPROVAL_STATUSES.has(String(status ?? '').trim().toLowerCase());
}

export function formatOrderStatusLabel(status: string): string {
  if (!status) return '';
  if (status === 'lost' || status === 'void') return 'Lost';
  return status
    .split('_')
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export type ResolvedOrderStage = {
  status: string;
  label: string;
  stageIndex: number | null;
};

export function deriveOrderStage(statuses: string[], lookup: OrderStageLookup): ResolvedOrderStage | null {
  const cleaned = statuses
    .map(status => (typeof status === 'string' ? status.trim() : ''))
    .filter((status): status is string => status.length > 0);
  if (cleaned.length === 0) {
    return null;
  }

  let best: ResolvedOrderStage | null = null;
  for (const status of cleaned) {
    const entry = lookup[status] ?? lookup[status.toLowerCase()];
    const stageIndex = entry?.stageIndex ?? null;
    const label = entry?.label?.trim() ? entry.label.trim() : formatOrderStatusLabel(status);
    const candidate: ResolvedOrderStage = {
      status,
      label,
      stageIndex,
    };

    if (!best) {
      best = candidate;
      continue;
    }

    const bestIndex = best.stageIndex ?? -1;
    const candidateIndex = stageIndex ?? -1;
    if (candidateIndex > bestIndex) {
      best = candidate;
      continue;
    }
    if (candidateIndex === bestIndex && candidate.label.localeCompare(best.label) < 0) {
      best = candidate;
    }
  }

  return best;
}
